/* AdminModerationButtons — small floating actions overlaid on directory/home
 * recipe & cookbook cards. Visible ONLY to logged-in Admin / SuperAdmin users.
 * On click → confirm → DELETE via the admin endpoint → call onDelete() so the
 * parent removes the card from its list.
 *
 * Usage:
 *   <AdminModerationButtons kind="recipe"   id={r.recipe_id}   onDelete={() => removeRecipe(r.recipe_id)} />
 *   <AdminModerationButtons kind="cookbook" id={c.cookbook_id} onDelete={() => removeCookbook(c.cookbook_id)} />
 */
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminModerationButtons({ kind, id, label, onDelete }) {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "SuperAdmin" || user.role === "Admin");
  if (!isAdmin) return null;

  const endpoint = kind === "cookbook" ? `/admin/cookbooks/${id}` : `/admin/recipes/${id}`;
  const noun = kind === "cookbook" ? "cookbook" : "recipe";

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Permanently delete this ${noun}${label ? ` (“${label}”)` : ""}?\n\nThis is an ADMIN MODERATION ACTION — the ${noun} is removed for everyone, irreversibly. The owner's recipe_count is decremented automatically.`)) return;
    try {
      await api.delete(endpoint);
      toast.success(`${noun[0].toUpperCase() + noun.slice(1)} deleted (admin)`);
      onDelete?.(id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`admin-mod-delete-${kind}-${id}`}
      title={`Delete this ${noun} (admin)`}
      className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-red-600/90 hover:bg-red-600 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
    >
      <Trash size={16} weight="bold" />
    </button>
  );
}
